// Reusable, Compass-branded .xlsx report building blocks — the generic half
// of the export capability, with no knowledge of Units/Leaders/anything
// domain-specific. Built directly on `write-excel-file` (already a Compass
// dependency — see exporters.ts's downloadXlsx()), not a new library.
// exporters.ts's own downloadXlsx() stays as-is for the existing flat
// multi-row CSV-style exports (Units/Leaders tables) — this file is for
// the different case: a small number of styled, structured "report" sheets
// (title, section headings, label/value pairs, formatted tables) that a
// uniform-column-width flat export can't produce.
//
// Deliberately thin: cell/row style PRESETS (title, section heading, label,
// value, table header, ...) plus a build/download pair. No sheet-content
// knowledge lives here — that's each report's own module (see
// unitExport.ts). Reuse this file for the next Compass export rather than
// duplicating its styling.

import type { Cell as WxfCell, CellObject as WxfCellObject, Row as WxfRow } from 'write-excel-file/browser';

// ---- Brand tokens (src/styles.css's own values — kept in sync by hand,
// same as every other place in this codebase that can't read CSS custom
// properties from a non-DOM context) --------------------------------------

export const BRAND = {
  navy: '#0e2c4a',
  blue: '#1769aa',
  ink: '#16324a',
  muted: '#6c7f91',
  line: '#d9e3ed',
  headerFill: '#eaf3fb', // --blue-soft
  white: '#ffffff',
} as const;

const FONT_FAMILY = 'Calibri';

export type ReportCell = WxfCell;
export type ReportRow = WxfRow;

export interface ReportColumn {
  width: number;
}

export interface ReportSheet {
  name: string;
  rows: ReportRow[];
  columns: ReportColumn[];
  /** Freezes this many top rows — set to the row index (1-based) of the
   *  last "always visible while scrolling" row, e.g. a table's own header
   *  row. Omit for short sheets that never need scrolling. */
  stickyRowsCount?: number;
  orientation?: 'portrait' | 'landscape';
}

// ---- Cell presets ---------------------------------------------------------
// Every report built on this file should compose its rows from these,
// rather than hand-writing style objects inline, so every Compass export
// shares one visual identity.

export function titleCell(value: string): WxfCellObject {
  return {
    value,
    type: String,
    fontWeight: 'bold',
    fontSize: 16,
    textColor: BRAND.navy,
    fontFamily: FONT_FAMILY,
  };
}

export function subtitleCell(value: string): WxfCellObject {
  return {
    value,
    type: String,
    fontSize: 12,
    textColor: BRAND.muted,
    fontFamily: FONT_FAMILY,
  };
}

/** A full-width section divider, e.g. "CHARTER / RECHARTER". */
export function sectionHeadingCell(value: string): WxfCellObject {
  return {
    value: value.toUpperCase(),
    type: String,
    fontWeight: 'bold',
    fontSize: 11,
    textColor: BRAND.white,
    backgroundColor: BRAND.navy,
    fontFamily: FONT_FAMILY,
  };
}

export function labelCell(value: string): WxfCellObject {
  return {
    value,
    type: String,
    textColor: BRAND.muted,
    fontFamily: FONT_FAMILY,
  };
}

/** A plain text/number value cell — `null`/`undefined`/`''` all render as a
 *  genuinely empty cell (never the string "null"/"undefined"). */
export function valueCell(value: string | number | null | undefined, opts: { wrap?: boolean } = {}): WxfCellObject {
  if (value == null || value === '') return { value: '', type: String, fontFamily: FONT_FAMILY };
  if (typeof value === 'number') {
    return { value, type: Number, fontFamily: FONT_FAMILY };
  }
  return { value, type: String, fontFamily: FONT_FAMILY, wrap: opts.wrap };
}

/** An integer/decimal count already computed elsewhere — never re-derives
 *  the number, just presents it. */
export function numberCell(value: number | null | undefined): WxfCellObject {
  if (value == null) return { value: '', type: String, fontFamily: FONT_FAMILY };
  return { value, type: Number, fontFamily: FONT_FAMILY, align: 'right' };
}

/** `value` is an already-computed whole or one-decimal percent (e.g. `73`
 *  or `73.5`), NOT a 0–1 fraction — formatted with a literal "%" suffix
 *  rather than Excel's implicit ×100 percentage format, so callers never
 *  have to remember to divide by 100 first. */
export function percentCell(value: number | null | undefined): WxfCellObject {
  if (value == null) return { value: '', type: String, fontFamily: FONT_FAMILY };
  const wholeNumber = Number.isInteger(value);
  return {
    value,
    type: Number,
    format: wholeNumber ? '0"%"' : '0.0"%"',
    fontFamily: FONT_FAMILY,
    align: 'right',
  };
}

/** `iso` is a `YYYY-MM-DD` string (Compass's `ISODateString` convention) —
 *  parsed as a LOCAL date (matches src/lib/unitHealth.ts's daysUntil(),
 *  never UTC) so the displayed date never shifts a day depending on the
 *  viewer's timezone. */
export function dateCell(iso: string | null | undefined, opts: { format?: string } = {}): WxfCellObject {
  if (!iso) return { value: '', type: String, fontFamily: FONT_FAMILY };
  const [y, m, d] = iso.split('-').map(Number);
  return {
    value: new Date(y, m - 1, d),
    type: Date,
    format: opts.format ?? 'mm/dd/yyyy',
    fontFamily: FONT_FAMILY,
  };
}

export function dateTimeCell(date: Date): WxfCellObject {
  return {
    value: date,
    type: Date,
    format: 'mm/dd/yyyy hh:mm AM/PM',
    fontFamily: FONT_FAMILY,
  };
}

export function tableHeaderCell(value: string): WxfCellObject {
  return {
    value,
    type: String,
    fontWeight: 'bold',
    textColor: BRAND.ink,
    backgroundColor: BRAND.headerFill,
    fontFamily: FONT_FAMILY,
    wrap: true,
    alignVertical: 'bottom',
  };
}

export function blankRow(): ReportRow {
  return [];
}

/** A single styled cell that visually spans the sheet's full column width
 *  (title/section-heading/note rows) — write-excel-file requires the
 *  spanned-over cells to still be present in the row array as `null`
 *  placeholders, or the merge silently mis-registers against whatever
 *  comes next; this is the one, single place that padding happens so no
 *  call site has to remember it. */
export function fullWidthRow(cell: WxfCellObject, totalColumns: number): ReportRow {
  return [{ ...cell, columnSpan: totalColumns }, ...(Array(Math.max(0, totalColumns - 1)).fill(null) as null[])];
}

/** A muted, italic-free note row — "No data available for this section."
 *  and similar. Never a raw empty table or an `undefined`. */
export function noteCell(value: string): WxfCellObject {
  return { value, type: String, textColor: BRAND.muted, fontFamily: FONT_FAMILY, wrap: true };
}

export const REPORT_FONT_FAMILY = FONT_FAMILY;
